import posthog from "posthog-js";
import { useSearchParams } from "react-router-dom";
import { PostIdeasEmptyState } from "./PostIdeasEmptyState";
import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from "react";
import { ExpandingWorkspace } from "./ExpandingWorkspace";
import { IDEAS_CHANGED_EVENT } from "../../hooks/useIdeasBadge";
import { useAuth } from "../../contexts/AuthContext";
import { collection, query, where, doc, onSnapshot } from "firebase/firestore";
import { useCollection } from "react-firebase-hooks/firestore";
import { db } from "../../services/Firebase";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { PlatformConnection } from "../../models/Platform";
import { BrandKit } from "../../models/BrandKit";
import { Post } from "../../models/Post";
import { TopicCard, PendingTopicData } from "./TopicCard";
import { PostViewer } from "../PostViewer";
import { PlatformConnectionIcon } from "../PlatformConnectionIcon";
import { useWorkspaceConnections } from "../../hooks/useWorkspaceConnections";
import { BrandKitSelector } from "../BrandKitSelector";
import { getBrandKitsForUser } from "../../lib/brandKitUtils";
import { POPULAR_LANGUAGES } from "../../constants/languages";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  ArrowDown,
  ArrowRight,
  Loader2,
  Rss,
  RefreshCw,
  Lightbulb,
  Rocket,
  AlertCircle,
  Pause,
  Play,
  Trash2,
  Pencil,
  Clock,
  Globe,
  Inbox,
  Sparkles,
  Archive,
  Plus,
  Check,
  User,
  ExternalLink,
} from "lucide-react";
import { postConverter } from "../../services/converters";
import { useStripe } from "../../contexts/StripeContext";
import { useUpgradePage } from "../../hooks/useUpgradePage";
import type { AutopilotConfig, IdeaGenerationSettings, SourceGenerationPresets } from "../../models/Autopilot";
import { resolveIdeaGenerationSettings } from "../../models/Autopilot";
import { IdeaGenerationWorkspace } from "./IdeaGenerationWorkspace";
import { SectionPicker, SectionPickerCandidate } from "./SectionPicker";

type SourceFallbackMode = "new_only" | "reuse_library";
type SourceType = "rss" | "sitemap" | "website" | "youtube";

interface MonitoringSource extends SourceGenerationPresets {
  id: string;
  name: string;
  url: string;
  sourceType?: SourceType;
  feedUrl?: string;
  sitemapUrl?: string;
  sectionPath?: string;
  selectedSitemapUrls?: string[];
  lastPolledAt?: number;
  isOwned?: boolean;
  fallbackMode?: SourceFallbackMode;
  status: "active" | "paused" | "error";
  extracting?: boolean;
  feedError?: string;
  extractionTotal?: number;
  extractionDone?: number;
  extractionErrors?: number;
  knownGuids?: string[];
  // Per-source config
  connectionIds?: string[];
  brandKitId?: string;
  targetLanguage?: string;
}

interface PendingSourceInput {
  url: string;
  sourceType: SourceType;
  feedUrl?: string;
  sitemapUrl?: string;
  sectionPath?: string;
  selectedSitemapUrls?: string[];
  feedTitle?: string;
  isOwned: boolean;
  fallbackMode?: SourceFallbackMode;
  recentItemCount?: number;
  totalItemCount?: number;
}

interface Props {
  linkedIdea?: PendingTopicData;
  onCloseLinkedIdea?: () => void;
  embedded?: boolean;
  /** The Ideas page: the full idea workspace in place, never a preview strip. */
  page?: boolean;
  /** Opens Drafts from a just-drafted idea on the Ideas page. */
  onOpenDrafts?: () => void;
  initiallyExpanded?: boolean;
  config: AutopilotConfig | null;
  configLoading?: boolean;
  pendingSource?: PendingSourceInput | null;
  onCancelPending?: () => void;
  onConfigUpdated: () => void;
}

function getDiscoverySummary(source: {
  sourceType?: SourceType;
  recentItemCount?: number;
  totalItemCount?: number;
}) {
  if (source.totalItemCount == null) return null;

  if (source.sourceType === "website") return `Found ${source.totalItemCount} website ${source.totalItemCount === 1 ? "page" : "pages"}`;
  if (source.sourceType === "youtube") return `Found ${source.totalItemCount} ${source.totalItemCount === 1 ? "video" : "videos"}`;

  const articleLabel = source.totalItemCount === 1 ? "article" : "articles";

  if (source.sourceType === "rss") {
    return `Found ${source.totalItemCount} recent ${articleLabel}`;
  }

  const recent =
    source.recentItemCount && source.recentItemCount > 0
      ? ` · ${source.recentItemCount} recent`
      : "";

  return `Found ${source.totalItemCount} ${articleLabel}${recent}`;
}

// Where the app used to ask for one section: say the source covers them all,
// and how to follow just one.
export function getSiteCoverageNote(url: string, sections?: string[]) {
  if (!sections || sections.length < 2) return null;
  const named = sections.slice(0, 3).map((section) => section.replace(/[-_]+/g, " "));
  const more = sections.length - named.length;
  const list =
    more > 0
      ? `${named.join(", ")} and ${more} more`
      : `${named.slice(0, -1).join(", ")} and ${named[named.length - 1]}`;
  let host = url;
  try {
    host = new URL(url).hostname.replace(/^www\./, "");
  } catch {
    // Keep the URL as typed.
  }
  return `Ideas will come from across the site: ${list}. To follow just one section, add its link instead, like ${host}/${sections[0]}.`;
}

// A saved source that follows a whole site: entered as a homepage, with no
// section. Its settings can't change the URL, so point to adding a section.
export function getWholeSiteSettingsNote(source: {
  url: string;
  sourceType?: SourceType;
  sectionPath?: string;
}) {
  if (source.sourceType !== "sitemap" && source.sourceType !== "website") return null;
  if (source.sectionPath && source.sectionPath !== "/") return null;
  let host: string;
  try {
    const url = new URL(source.url);
    if (url.pathname.replace(/\/+$/, "") !== "") return null;
    host = url.hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
  return `Ideas come from across the whole site. To follow one section, add that section's link instead: ${host}/blog`;
}

function defaultFallbackMode(isOwned: boolean | null): SourceFallbackMode {
  return isOwned === true ? "reuse_library" : "new_only";
}

// Mirrors the server's hourly Check now limit; the server enforces it.
const CHECK_NOW_COOLDOWN_MS = 60 * 60 * 1000;

type CheckNowResult =
  | { status: "started"; newArticles: number; libraryArticles: number; libraryRemaining: number; libraryNextAt?: number; nextCheckAt: number }
  | { status: "nothing_new"; previousCheckAt?: number; failedSources: number; waitForNewSourceIds: string[]; libraryNextAt?: number; nextCheckAt: number }
  | { status: "cooldown"; nextCheckAt: number }
  | { status: "busy" }
  | { status: "idea_cap"; cap: number }
  | { status: "no_active_sources" };

const countOf = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;
const clockTime = (timestamp: number) =>
  new Date(timestamp).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

function fallbackModeFooter(mode?: SourceFallbackMode) {
  if (mode === "reuse_library") {
    return "We'll keep checking for new content and reuse older articles when there's nothing new.";
  }
  return "We'll monitor this source for newly published articles.";
}

export function AutopilotFeed({
  linkedIdea,
  onCloseLinkedIdea,
  embedded: embeddedProp = false,
  page = false,
  onOpenDrafts,
  initiallyExpanded = false,
  config,
  configLoading = false,
  pendingSource,
  onCancelPending,
  onConfigUpdated,
}: Props) {
  const { currentUser, activeWorkspaceId, customerData } = useAuth();
  const { isStarterOrAbove, isLoading: planLoading } = useStripe();
  const outOfCredits =
    !!customerData &&
    (customerData.videoCredits ?? 0) + (customerData.bonusCredits ?? 0) <= 0;

  // The page reuses the expanded workspace's cards, sources and filters.
  const embedded = embeddedProp || page;
  const [expanded, setExpanded] = useState(initiallyExpanded || page);
  const [selectedIdeaId, setSelectedIdeaId] = useState<string | null>(linkedIdea?.id || null);
  const changeIdeaPreview = (id: string, open: boolean) => {
    setSelectedIdeaId(open ? id : null);
    if (!open && id === linkedIdea?.id) onCloseLinkedIdea?.();
  };

  // Data state
  const [sources, setSources] = useState<MonitoringSource[]>([]);
  const [sourcesWorkspace, setSourcesWorkspace] = useState<string | null>(null);
  const [stats, setStats] = useState<Record<string, number>>({
    pending: 0,
    generated: 0,
    archived: 0,
  });
  const [loading, setLoading] = useState(true);
  const [ideasError, setIdeasError] = useState(false);
  const [ideasRetry, setIdeasRetry] = useState(0);
  const [checkingNow, setCheckingNow] = useState(false);
  const [usingPastArticles, setUsingPastArticles] = useState(false);
  const [checkResult, setCheckResult] = useState<CheckNowResult | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [, setClockTick] = useState(0);
  const [displayLimit, setDisplayLimit] = useState(20);
  const [anyExtracting, setAnyExtracting] = useState(false);
  const { navigateToUpgrade } = useUpgradePage();

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>(
    embedded ? "pending" : "all",
  );
  const [sourceFilter, setSourceFilter] = useState<string>("all");

  // Post viewer state
  const [viewingPostId, setViewingPostId] = useState<string | null>(null);
  const [viewingPost, setViewingPost] = useState<Post | null>(null);

  const { connections: workspaceConnections } = useWorkspaceConnections();

  const connectionsById = useMemo(() => {
    const connections: Record<string, PlatformConnection> = {};
    workspaceConnections.forEach((connection) => {
      connections[connection.id] = connection;
    });
    return connections;
  }, [workspaceConnections]);

  // Fetch brand kits for source cards
  const [userBrandKits, setUserBrandKits] = useState<BrandKit[]>([]);
  useEffect(() => {
    let cancelled = false;
    setUserBrandKits([]);
    if (currentUser && activeWorkspaceId) {
      getBrandKitsForUser(activeWorkspaceId)
        .then((kits) => { if (!cancelled) setUserBrandKits(kits); })
        .catch(console.error);
    }
    return () => { cancelled = true; };
  }, [currentUser, activeWorkspaceId]);

  // Unified source modal state (add + edit)
  const [sourceModal, setSourceModal] = useState<{
    open: boolean;
    mode: "add" | "edit" | "activate";
    sourceId?: string; // set when editing
    url: string;
    isOwned: boolean | null;
    fallbackMode: SourceFallbackMode;
    connectionIds: string[];
    brandKitId: string | null;
    targetLanguage: string;
    // discovery state
    discovered: boolean; // URL has been validated
    sourceType?: SourceType;
    feedUrl?: string;
    sitemapUrl?: string;
    sectionPath?: string;
    selectedSitemapUrls?: string[];
    feedTitle?: string;
    recentItemCount?: number;
    totalItemCount?: number;
    sections?: string[];
  }>({
    open: false,
    mode: "add",
    url: "",
    isOwned: null,
    fallbackMode: "new_only",
    connectionIds: [],
    brandKitId: null,
    targetLanguage: "auto",
    discovered: false,
  });
  const [discovering, setDiscovering] = useState(false);
  const sourceUrlEdited = useRef(false);
  const firstSourceBrandKit = sources.length === 0
    ? userBrandKits.find((kit) => kit.website?.trim())
    : undefined;
  // Brand kits may arrive after the dialog opens. Never replace user input.
  useEffect(() => {
    if (!firstSourceBrandKit || sourceUrlEdited.current) return;
    setSourceModal((prev) =>
      prev.open && prev.mode === "add" && !prev.discovered && !prev.url
        ? { ...prev, url: firstSourceBrandKit.website!.trim(), brandKitId: firstSourceBrandKit.id }
        : prev,
    );
  }, [firstSourceBrandKit, sourceModal.open]);
  const [discoverError, setDiscoverError] = useState<string | null>(null);
  const [savingSource, setSavingSource] = useState(false);
  // Populated when sitemap discovery returns multiple sections for the user to pick.
  const [sectionCandidates, setSectionCandidates] = useState<
    SectionPickerCandidate[] | null
  >(null);

  // Auto-open modal for pending source from setup flow
  useEffect(() => {
    if (pendingSource) {
      setSourceModal({
        open: true,
        mode: "activate",
        url: pendingSource.url,
        isOwned: pendingSource.isOwned,
        fallbackMode:
          pendingSource.fallbackMode ||
          defaultFallbackMode(pendingSource.isOwned),
        connectionIds: [],
        brandKitId: null,
        targetLanguage: "auto",
        discovered: true,
        sourceType: pendingSource.sourceType,
        feedUrl: pendingSource.feedUrl,
        sitemapUrl: pendingSource.sitemapUrl,
        sectionPath: pendingSource.sectionPath,
        selectedSitemapUrls: pendingSource.selectedSitemapUrls,
        feedTitle: pendingSource.feedTitle,
        recentItemCount: pendingSource.recentItemCount,
        totalItemCount: pendingSource.totalItemCount,
      });
    }
  }, [pendingSource]);

  const openAddSourceModal = () => {
    if (planLoading || sourcesWorkspace !== activeWorkspaceId) return;
    if (!isStarterOrAbove && sources.length >= 1) {
      navigateToUpgrade({ reason: "autopilot_add_source" });
      return;
    }
    const lastSource = sources[0];
    sourceUrlEdited.current = false;
    setSourceModal({
      open: true,
      mode: "add",
      url: firstSourceBrandKit?.website?.trim() || "",
      isOwned: true,
      fallbackMode: defaultFallbackMode(true),
      connectionIds: lastSource?.connectionIds || [],
      brandKitId: lastSource?.brandKitId || firstSourceBrandKit?.id || null,
      targetLanguage: lastSource?.targetLanguage || "auto",
      discovered: false,
    });
    setDiscoverError(null);
    setSectionCandidates(null);
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const ideasRequested = searchParams.get("ideas") === "1";
  useEffect(() => {
    if (embedded && ideasRequested) setExpanded(true);
  }, [embedded, ideasRequested]);
  const addSourceRequested = searchParams.get("addSource") === "1";
  const handledAddSource = useRef(false);
  useEffect(() => {
    if (!addSourceRequested) { handledAddSource.current = false; return; }
    if (!embedded || handledAddSource.current || configLoading || planLoading || sourcesWorkspace !== activeWorkspaceId) return;
    handledAddSource.current = true;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("addSource");
      return next;
    }, { replace: true });
    openAddSourceModal();
    // Open once per explicit navigation, using the current modal defaults.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, addSourceRequested, setSearchParams, configLoading, planLoading, sourcesWorkspace, activeWorkspaceId, isStarterOrAbove, sources]);

  const openEditSourceModal = (source: MonitoringSource) => {
    setSourceModal({
      open: true,
      mode: "edit",
      sourceId: source.id,
      url: source.url,
      isOwned: source.isOwned ?? false,
      fallbackMode: source.fallbackMode || "new_only",
      connectionIds: source.connectionIds || [],
      brandKitId: source.brandKitId || null,
      targetLanguage: source.targetLanguage || "auto",
      discovered: true,
      sourceType: source.sourceType,
      feedUrl: source.feedUrl,
      sitemapUrl: source.sitemapUrl,
      sectionPath: source.sectionPath,
      selectedSitemapUrls: source.selectedSitemapUrls,
      // A website's stored name is its homepage title; its domain reads clearer.
      feedTitle: (() => {
        if (source.sourceType !== "website" && source.name) return source.name;
        try { return new URL(source.url).hostname.replace(/^www\./, ""); }
        catch { return source.name; }
      })(),
    });
    setDiscoverError(null);
    setSectionCandidates(null);
  };

  const closeSourceModal = () => {
    setSourceModal((prev) => ({ ...prev, open: false }));
    setDiscoverError(null);
    setSectionCandidates(null);
  };

  // Fetch post for viewer
  const [postData] = useDocumentData(
    viewingPostId
      ? doc(db, "posts", viewingPostId).withConverter(postConverter)
      : null,
  );

  useEffect(() => {
    if (postData) {
      setViewingPost(postData as Post);
    }
  }, [postData]);

  const apiCall = useCallback(
    async (path: string, options?: RequestInit) => {
      if (!currentUser) throw new Error("Not authenticated");
      const token = await currentUser.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_API_BASE}${path}`, {
        ...options,
        headers: {
          ...options?.headers,
          Authorization: `Bearer ${token}`,
          "x-workspace-id": activeWorkspaceId || "",
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const err = new Error(data.error || `Request failed: ${res.status}`);
        (err as any).status = res.status;
        (err as any).code = data.code;
        (err as any).settings = data.settings;
        throw err;
      }
      return res.json();
    },
    [currentUser, activeWorkspaceId],
  );

  // The compact preview needs pending ideas only. Load history on expansion.
  const [rawTopics, setRawTopics] = useState<PendingTopicData[]>([]);
  const pendingPreview = embedded && !expanded;

  useEffect(() => {
    if (!currentUser) return;

    setLoading(true);
    setIdeasError(false);

    const q = query(
      collection(db, "pending_topics"),
      where("userId", "==", activeWorkspaceId),
      ...(pendingPreview ? [where("status", "==", "pending")] : []),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const all = snapshot.docs.map(
          (d) => ({ id: d.id, ...d.data() }) as PendingTopicData,
        );
        setRawTopics(all);

        // Compute stats from full set
        const counts = { pending: 0, generated: 0, archived: 0 };
        for (const t of all) {
          if (t.status in counts) counts[t.status as keyof typeof counts]++;
        }
        setStats(counts);
        setLoading(false);
      },
      (err) => {
        console.error("Topics listener error:", err);
        setIdeasError(true);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser, activeWorkspaceId, ideasRetry, pendingPreview]);

  // Filter and sort topics based on current filters
  const allTopics = useMemo(() => {
    let filtered = rawTopics;
    if (statusFilter && statusFilter !== "all") {
      filtered = filtered.filter((t) => t.status === statusFilter);
    }
    if (sourceFilter && sourceFilter !== "all") {
      filtered = filtered.filter((t) => t.sourceId === sourceFilter);
    }
    const getSortTime = (t: PendingTopicData) => {
      if (statusFilter === "generated") return t.generatedAt || t.discoveredAt;
      return t.articlePublishedAt || t.discoveredAt;
    };
    return filtered.sort((a, b) => getSortTime(b) - getSortTime(a));
  }, [rawTopics, statusFilter, sourceFilter]);

  // Drafted ideas stay in their spot briefly to point at the new draft.
  const [handoffs, setHandoffs] = useState<
    Record<string, { topic: PendingTopicData; index: number; drafted: boolean }>
  >({});
  const handoffTimers = useRef(new Set<ReturnType<typeof setTimeout>>());
  useEffect(() => {
    setHandoffs({});
    const timers = handoffTimers.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, [activeWorkspaceId]);

  // Slice topics by displayLimit
  const holdsDrafted = (embedded && !expanded) || (page && statusFilter === "pending");
  const visibleTopics = useMemo(() => {
    if (!holdsDrafted) return allTopics.slice(0, displayLimit);
    // A held idea keeps the exact spot it was drafted from.
    const row = [...allTopics];
    Object.values(handoffs)
      .filter((handoff) => !row.some((t) => t.id === handoff.topic.id))
      .sort((a, b) => a.index - b.index)
      .forEach((handoff) => row.splice(handoff.index, 0, handoff.topic));
    return page ? row.slice(0, displayLimit + Object.keys(handoffs).length) : row.slice(0, 6);
  }, [allTopics, handoffs, displayLimit, holdsDrafted, page]);

  // Email links must also open old/archived ideas outside the current filter
  // or first page. Prefer the live record when the subscription catches up.
  const topics = linkedIdea
    ? [rawTopics.find((topic) => topic.id === linkedIdea.id) || linkedIdea,
       ...visibleTopics.filter((topic) => topic.id !== linkedIdea.id)]
    : visibleTopics;

  const totalTopics = allTopics.length;

  // Load more just increases the display limit
  const loadMore = useCallback(() => {
    setDisplayLimit((prev) => prev + 20);
  }, []);

  // Reset display limit when filters change
  useEffect(() => {
    setDisplayLimit(20);
  }, [statusFilter, sourceFilter]);

  // ---- Real-time listener for sources (includes extracting state) ----
  useEffect(() => {
    if (!currentUser) return;

    const q = query(
      collection(db, "monitoring_sources"),
      where("userId", "==", activeWorkspaceId),
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }) as MonitoringSource)
        .sort((a, b) => (b as any).createdAt - (a as any).createdAt);
      setSources(data);
      setSourcesWorkspace(activeWorkspaceId);
      setAnyExtracting(data.some((s) => s.extracting));
    });
    return () => unsubscribe();
  }, [currentUser, activeWorkspaceId]);

  // Action handlers
  const [configuringIdea, setConfiguringIdea] = useState<PendingTopicData | null>(null);
  const [configurationRecovery, setConfigurationRecovery] = useState<{ message: string; settings: IdeaGenerationSettings } | null>(null);
  useEffect(() => {
    setConfiguringIdea(null);
    setConfigurationRecovery(null);
  }, [activeWorkspaceId]);
  const generationDefaults = (topic: PendingTopicData) => {
    const source = sources.find(s => s.id === topic.sourceId);
    const brandKitId = source?.brandKitId ?? config?.brandKitId ?? null;
    const saved = resolveIdeaGenerationSettings(source, topic.content.category, brandKitId);
    return { saved, defaults: saved ?? {
      brandKitId,
      // Accounts are chosen when publishing, not when drafting an idea.
      connectionIds: [],
      templateIds: [],
      visualStyleId: "auto" as const,
    } };
  };
  // Previews show the brand photos already chosen for each slide.
  const brandAssetsFor = (topic: PendingTopicData) => {
    const source = sources.find(s => s.id === topic.sourceId);
    const brandKitId = source?.brandKitId ?? config?.brandKitId ?? null;
    return userBrandKits.find(kit => kit.id === brandKitId)?.brandAssets;
  };
  const configureIdea = (topic: PendingTopicData) => {
    setConfigurationRecovery(null);
    setSelectedIdeaId(null);
    setConfiguringIdea(topic);
  };
  const handleGenerate = async (topicId: string, settings?: IdeaGenerationSettings) => {
    const startedAt = Date.now();
    // "Draft this" works before any settings are saved: use the same defaults
    // Customize starts from. Accounts are chosen when publishing.
    const unsavedTopic = settings ? undefined : rawTopics.find((t) => t.id === topicId);
    if (unsavedTopic && !generationDefaults(unsavedTopic).saved) {
      settings = generationDefaults(unsavedTopic).defaults;
    }
    posthog.capture("generation_started", {
      entry_point: "post_idea",
      source_count: 1,
      source_types: ["url"],
      mode: "single",
    });
    try {
      const result = await apiCall(
        `/api/autopilot/topics/${topicId}/generate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(settings ? { settings } : { useLastSettings: true }),
        },
      );
      posthog.capture("generation_request_returned", {
        entry_point: "post_idea",
        duration_ms: Date.now() - startedAt,
        source_count: 1,
        post_count: result.postId ? 1 : 0,
      });
      return result;
    } catch (err: any) {
      if (!settings && err.code === 'BRAND_KIT_DELETED') {
        const topic = rawTopics.find(topic => topic.id === topicId);
        if (topic) {
          setSelectedIdeaId(null);
          setConfigurationRecovery({ message: err.message, settings: err.settings });
          setConfiguringIdea(topic);
          // Refresh the selector too: this kit may have been deleted in another tab.
          if (activeWorkspaceId) {
            void getBrandKitsForUser(activeWorkspaceId).then(setUserBrandKits).catch(console.error);
          }
          err.handled = true;
        }
      }
      if (err.status === 402) {
        navigateToUpgrade({ reason: "no_credits" });
      }
      throw err;
    }
  };

  // In the compact row, hold the card in place while the draft starts, then
  // point at Recent drafts for a moment before the next idea takes its turn.
  const handleRowGenerate = async (
    topicId: string,
    settings?: IdeaGenerationSettings,
  ) => {
    const index = topics.findIndex((t) => t.id === topicId);
    if (index >= 0) {
      setHandoffs((prev) => ({
        ...prev,
        [topicId]: { topic: topics[index], index, drafted: false },
      }));
    }
    try {
      const result = await handleGenerate(topicId, settings);
      setHandoffs((prev) =>
        prev[topicId]
          ? { ...prev, [topicId]: { ...prev[topicId], drafted: true } }
          : prev,
      );
      const timer = setTimeout(() => {
        handoffTimers.current.delete(timer);
        setHandoffs((prev) => {
          const { [topicId]: _done, ...rest } = prev;
          return rest;
        });
      }, page ? PAGE_HANDOFF_MS : HANDOFF_MS);
      handoffTimers.current.add(timer);
      return result;
    } catch (err) {
      setHandoffs((prev) => {
        const { [topicId]: _failed, ...rest } = prev;
        return rest;
      });
      throw err;
    }
  };

  const handleArchive = async (topicId: string) => {
    await apiCall(`/api/autopilot/topics/${topicId}/archive`, {
      method: "POST",
    });
    if (embedded) {
      setSelectedIdeaId((current) => (current === topicId ? null : current));
    }
  };

  const handleUnarchive = async (topicId: string) => {
    await apiCall(`/api/autopilot/topics/${topicId}/unarchive`, {
      method: "POST",
    });
  };

  const handleViewPost = (postId: string) => {
    setViewingPostId(postId);
  };

  const handlePauseResume = async () => {
    if (configLoading) return;
    // Lite and free users must upgrade to enable autopilot
    if (!config?.enabled && !isStarterOrAbove) {
      navigateToUpgrade({ reason: "autopilot_activate" });
      return;
    }
    await apiCall("/api/autopilot/config", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled: !config?.enabled }),
    });
    onConfigUpdated();
  };

  // The later of the saved check time and the last server answer, so the
  // button locks immediately rather than after config reloads.
  const nextCheckAt = Math.max(
    config?.lastManualCheckAt ? config.lastManualCheckAt + CHECK_NOW_COOLDOWN_MS : 0,
    checkResult && "nextCheckAt" in checkResult ? checkResult.nextCheckAt : 0,
  );
  const checkCoolingDown = nextCheckAt > Date.now();
  useEffect(() => {
    if (!checkCoolingDown) return;
    const timer = setInterval(() => setClockTick((tick) => tick + 1), 30000);
    return () => clearInterval(timer);
  }, [checkCoolingDown]);

  // The result note describes a check in progress; drop it once reading ends.
  const checkSawReading = useRef(false);
  useEffect(() => {
    if (checkResult?.status !== "started") {
      checkSawReading.current = false;
      return;
    }
    if (anyExtracting) checkSawReading.current = true;
    else if (checkSawReading.current) {
      checkSawReading.current = false;
      setCheckResult(null);
    }
  }, [anyExtracting, checkResult]);

  const handleCheckNow = async () => {
    if (checkingNow || checkCoolingDown) return;
    setCheckingNow(true);
    setCheckError(null);
    setCheckResult(null);
    try {
      setCheckResult(await apiCall("/api/autopilot/check-now", { method: "POST" }));
      onConfigUpdated();
    } catch (error: any) {
      setCheckError(
        error?.status && error.status < 500 && error.message
          ? error.message
          : "We couldn’t check your sources just now. Try again in a moment.",
      );
    } finally {
      setCheckingNow(false);
    }
  };

  // From an empty check: switch "Wait for new" sources to reuse older articles
  // and draft from past articles now. New articles still come first.
  const handleUsePastArticles = async (sourceIds: string[]) => {
    if (usingPastArticles) return;
    setUsingPastArticles(true);
    setCheckError(null);
    try {
      setCheckResult(await apiCall("/api/autopilot/use-past-articles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sourceIds }),
      }));
      onConfigUpdated();
    } catch (error: any) {
      setCheckError(
        error?.status && error.status < 500 && error.message
          ? error.message
          : "We couldn’t get ideas from past articles just now. Try again in a moment.",
      );
    } finally {
      setUsingPastArticles(false);
    }
  };

  const callDiscoverApi = async (url: string, sectionPath?: string) => {
    const token = await currentUser!.getIdToken();
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE}/api/autopilot/discover`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ url, sectionPath }),
      },
    );
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error || "Failed to discover feed");
    }
    return res.json();
  };

  const applyDiscoveryResult = (url: string, result: any) => {
    setSourceModal((prev) => ({
      ...prev,
      url,
      discovered: true,
      sourceType: result.sourceType,
      feedUrl: result.feedUrl,
      sitemapUrl: result.sitemapUrl,
      sectionPath: result.sectionPath,
      selectedSitemapUrls: result.sitemapUrl
        ? result.selectedSitemapUrls
        : undefined,
      feedTitle: result.feedTitle,
      recentItemCount: result.recentItemCount,
      totalItemCount: result.totalItemCount,
      sections: result.sections,
    }));
    setSectionCandidates(null);
  };

  const handleDiscoverFeed = async () => {
    if (!sourceModal.url.trim() || !currentUser) return;
    let url = sourceModal.url.trim();
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;

    setDiscovering(true);
    setDiscoverError(null);
    try {
      const result = await callDiscoverApi(url);
      // A redirect or Instagram link is followed under the address it led to.
      if (result.resolvedUrl) url = result.resolvedUrl;
      if (result.needsSectionConfirmation) {
        setSourceModal((prev) => ({ ...prev, url }));
        setSectionCandidates(result.candidates || []);
      } else {
        applyDiscoveryResult(url, result);
      }
    } catch (err: any) {
      setDiscoverError(err.message);
    } finally {
      setDiscovering(false);
    }
  };

  const handleSectionSelectInModal = async (sectionPath: string) => {
    if (!currentUser || !sourceModal.url) return;
    setDiscovering(true);
    setDiscoverError(null);
    try {
      const result = await callDiscoverApi(sourceModal.url, sectionPath);
      if (result.needsSectionConfirmation) {
        setSectionCandidates(result.candidates || []);
      } else {
        applyDiscoveryResult(sourceModal.url, result);
      }
    } catch (err: any) {
      setDiscoverError(err.message);
    } finally {
      setDiscovering(false);
    }
  };

  const handleSaveSourceModal = async () => {
    if (configLoading || planLoading || sourcesWorkspace !== activeWorkspaceId) return;
    const m = sourceModal;
    if (m.mode === "add" && !isStarterOrAbove && sources.length >= 1) {
      setSourceModal(prev => ({ ...prev, open: false }));
      navigateToUpgrade({ reason: "autopilot_add_source" });
      return;
    }
    if (m.isOwned === null) return;

    setSavingSource(true);
    setDiscoverError(null);
    try {
      const modalEntry = {
        url: m.url,
        sourceType: m.sourceType || "rss",
        feedUrl: m.feedUrl,
        sitemapUrl: m.sitemapUrl,
        sectionPath: m.sectionPath,
        selectedSitemapUrls: m.selectedSitemapUrls,
        feedTitle: m.feedTitle,
        name: m.feedTitle || new URL(m.url).hostname,
        isOwned: m.isOwned,
        fallbackMode: m.fallbackMode,
        connectionIds: m.connectionIds,
        brandKitId: m.brandKitId,
        targetLanguage: m.targetLanguage !== "auto" ? m.targetLanguage : null,
      };
      if (m.mode === "activate" || (m.mode === "add" && !config)) {
        // First-time setup: create config + source + enable
        await apiCall("/api/autopilot/config", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            enabled: isStarterOrAbove,
            sourceUrls: [modalEntry],
          }),
        });
        onConfigUpdated();
      } else if (m.mode === "add") {
        // Add source to existing config
        await apiCall("/api/autopilot/config", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sourceUrls: [
              ...sources.map((s) => ({
                url: s.url,
                sourceType: s.sourceType || "rss",
                feedUrl: s.feedUrl,
                sitemapUrl: s.sitemapUrl,
                sectionPath: s.sectionPath,
                selectedSitemapUrls: s.selectedSitemapUrls,
                name: s.name,
                isOwned: s.isOwned || false,
                fallbackMode: s.fallbackMode || "new_only",
                connectionIds: s.connectionIds || [],
                brandKitId: s.brandKitId,
                targetLanguage: s.targetLanguage,
              })),
              modalEntry,
            ],
          }),
        });
        onConfigUpdated();
      } else {
        // Edit existing source config
        await apiCall(`/api/autopilot/sources/${m.sourceId}/config`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            connectionIds: m.connectionIds,
            brandKitId: m.brandKitId,
            targetLanguage:
              m.targetLanguage !== "auto" ? m.targetLanguage : null,
            isOwned: m.isOwned,
            fallbackMode: m.fallbackMode,
            selectedSitemapUrls: m.selectedSitemapUrls,
          }),
        });
      }
      closeSourceModal();
    } catch (err: any) {
      setDiscoverError(err.message);
    } finally {
      setSavingSource(false);
    }
  };

  const handleDeleteSource = async (sourceId: string) => {
    setSavingSource(true);
    try {
      await apiCall(`/api/autopilot/sources/${sourceId}/delete`, {
        method: "POST",
      });
      closeSourceModal();
      if (sourceFilter === sourceId) setSourceFilter("all");
      onConfigUpdated();
    } catch (err: any) {
      setDiscoverError(err.message);
    } finally {
      setSavingSource(false);
    }
  };

  const totalTopicCount = stats.pending + stats.generated + stats.archived;

  // Compute polling info from sources + topics
  const pollInfo = useMemo(() => {
    const activeSources = sources.filter((s) => s.lastPolledAt);
    if (activeSources.length === 0) return null;
    const lastPolledAt = Math.max(...activeSources.map((s) => s.lastPolledAt!));
    const topicsFromLastPoll = rawTopics.filter(
      (t) => t.discoveredAt >= lastPolledAt,
    ).length;
    const nextPollAt = lastPolledAt + 24 * 60 * 60 * 1000;
    return { lastPolledAt, topicsFromLastPoll, nextPollAt };
  }, [sources, rawTopics]);

  const formatRelativeTime = (timestamp: number) => {
    const diff = Math.abs(Date.now() - timestamp);
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const formatTimeUntil = (timestamp: number) => {
    const diff = timestamp - Date.now();
    if (diff <= 0) return "soon";
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) {
      const minutes = Math.floor(diff / 60000);
      return `~${minutes}m`;
    }
    return `~${hours}h`;
  };

  const extractingSources = sources.filter(
    (source) => source.extracting && !source.feedError,
  );
  // A channel is named for itself; every one lives on youtube.com.
  const extractingDomains = Array.from(new Set(extractingSources.flatMap((source) => {
    if (source.sourceType === "youtube" && source.name) return [source.name];
    try { return [new URL(source.url).hostname.replace(/^www\./, "")]; }
    catch { return []; }
  })));
  const extractionUnit = extractingSources.every((source) => source.sourceType === "youtube") ? "videos" : "pages";
  const discoveryContext = extractingDomains.length > 2
    ? `${extractingDomains[0]} and ${extractingDomains.length - 1} other sources`
    : extractingDomains.join(" and ") || "your sources";
  const extractionTotalKnown =
    extractingSources.length > 0 &&
    extractingSources.every((source) => (source.extractionTotal || 0) > 0);
  const extractionTotal = extractingSources.reduce(
    (total, source) => total + (source.extractionTotal || 0),
    0,
  );
  const extractionDone = extractingSources.reduce(
    (done, source) =>
      done + Math.min(source.extractionDone || 0, source.extractionTotal || 0),
    0,
  );
  const compactProgress = topics.length > 0;
  const extractionProgress = (
    <div className={compactProgress
      ? "mb-5 flex flex-col items-center gap-2 px-4 py-2 text-center text-muted-foreground"
      : "mx-auto max-w-md px-4 py-8 text-center"}>
      <p
        role="status"
        className="flex min-w-0 items-center justify-center gap-2 text-sm text-muted-foreground"
      >
        <Loader2
          className="h-3.5 w-3.5 shrink-0 animate-spin motion-reduce:animate-none"
          aria-hidden="true"
        />
        <span className="min-w-0 break-words">
          Finding {compactProgress ? "more ideas" : "ideas"} from {discoveryContext}…
        </span>
      </p>
      {extractionTotalKnown && (
        <div className={compactProgress
          ? "flex items-center gap-2"
          : "mx-auto mt-4 max-w-xs"}>
          <div
            role="progressbar"
            aria-label="Articles processed"
            aria-valuemin={0}
            aria-valuemax={extractionTotal}
            aria-valuenow={extractionDone}
            className={`h-1 overflow-hidden rounded-full bg-border ${compactProgress ? "w-20" : "w-full"}`}
          >
            <div
              className="h-full origin-left rounded-full bg-brand opacity-70 transition-transform duration-500 motion-reduce:transition-none"
              style={{ transform: `scaleX(${extractionDone / extractionTotal})` }}
            />
          </div>
          <p className={`text-xs tabular-nums text-muted-foreground ${compactProgress ? "" : "mt-2"}`}>
            {compactProgress
              ? <><span aria-hidden="true">{extractionDone}/{extractionTotal}</span><span className="sr-only">{extractionDone} of {extractionTotal} articles processed</span></>
              : `${extractionDone} of ${extractionTotal} articles processed`}
          </p>
        </div>
      )}
    </div>
  );

  // Keep the sidebar's idea count in step with drafting, dismissing and restoring.
  useEffect(() => {
    window.dispatchEvent(new Event(IDEAS_CHANGED_EVENT));
  }, [stats.pending]);

  // ---- Ideas page: plain-language status and where ideas come from ----
  // A channel is named for itself; every one lives on youtube.com.
  const sourceHost = (source: MonitoringSource) => {
    if (source.sourceType === "youtube" && source.name) return source.name;
    try { return new URL(source.url).hostname.replace(/^www\./, ""); }
    catch { return source.name; }
  };
  // A website's stored name is its homepage title; its domain reads clearer.
  const sourceLabelFor = (topic: PendingTopicData) => {
    const source = sources.find((s) => s.id === topic.sourceId);
    return source && (source.sourceType === "website" || !source.name) ? sourceHost(source) : undefined;
  };
  const sourceHosts = Array.from(new Set(sources.map(sourceHost)));
  const sourcesPhrase = sourceHosts.length > 2
    ? `${sourceHosts[0]} and ${sourceHosts.length - 1} other sources`
    : sourceHosts.join(" and ");
  const foundSentence = stats.pending === 0
    ? `No new ideas from ${sourcesPhrase} right now.`
    : `We found ${stats.pending === 1 ? "1 idea" : `${stats.pending} ideas`} on ${sourcesPhrase}`;
  // Joins the found sentence with what happens next.
  const foundWith = (next: string) =>
    stats.pending === 0 ? `${foundSentence} ${next}` : `${foundSentence}${next.startsWith("and ") ? ` ${next}` : `. ${next}`}`;
  const statusLink = (label: string, onClick: () => void) => (
    <Button variant="link" className="h-auto shrink-0 p-0 text-sm" onClick={onClick}>
      {label}
    </Button>
  );
  const upgradeButton = (
    <Button
      size="sm"
      className="shrink-0 bg-brand text-brand-foreground hover:bg-brand/90"
      onClick={() => navigateToUpgrade({ reason: "autopilot_activate" })}
    >
      Upgrade
    </Button>
  );
  const checkCaptionId = "check-now-availability";
  const dailyCheckActions = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {checkCoolingDown && !checkingNow && (
        <span id={checkCaptionId} className="text-xs text-muted-foreground">
          Checked {formatRelativeTime(nextCheckAt - CHECK_NOW_COOLDOWN_MS)} · available again at {clockTime(nextCheckAt)}
        </span>
      )}
      <Button
        variant="outline"
        size="sm"
        className="shrink-0"
        onClick={handleCheckNow}
        disabled={checkingNow || checkCoolingDown}
        aria-describedby={checkCoolingDown && !checkingNow ? checkCaptionId : undefined}
      >
        {checkingNow && <Loader2 aria-hidden="true" className="mr-1.5 h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />}
        {checkingNow ? "Checking…" : "Check now"}
      </Button>
      {statusLink("Pause daily checks", handlePauseResume)}
    </div>
  );
  const waitForNewSource = checkResult?.status === "nothing_new"
    ? sources.find((source) => checkResult.waitForNewSourceIds.includes(source.id))
    : undefined;
  const pastArticles = (count: number) => countOf(count, "past article");
  const checkNotice: ReactNode = checkError
    ? checkError
    : !checkResult
      ? null
      : checkResult.status === "started"
        ? checkResult.newArticles
          ? `Found ${countOf(checkResult.newArticles, "new article")}. We’re drafting ideas from ${
              checkResult.newArticles === 1 ? "it" : "them"}${
              checkResult.libraryArticles ? ` and ${pastArticles(checkResult.libraryArticles)}` : " now"}.`
          : `No new articles, so we’re drafting ideas from ${pastArticles(checkResult.libraryArticles)}.`
        : checkResult.status === "nothing_new"
          ? <>
              No new articles{checkResult.previousCheckAt ? ` since the last check ${formatRelativeTime(checkResult.previousCheckAt)}` : ""}
              {checkResult.waitForNewSourceIds.length === sources.filter((source) => source.status === "active").length && waitForNewSource
                ? <>. Want ideas from past articles too? New ones will still come first.{" "}
                    <Button
                      variant="link"
                      className="h-auto shrink-0 p-0 text-sm"
                      disabled={usingPastArticles}
                      onClick={() => handleUsePastArticles(checkResult.waitForNewSourceIds)}
                    >
                      {usingPastArticles && <Loader2 aria-hidden="true" className="mr-1 h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />}
                      {usingPastArticles ? "Getting ideas…" : "Use past articles"}
                    </Button></>
                : checkResult.libraryNextAt
                  ? ", and all your past articles were used for ideas in the last day. Check back tomorrow."
                  : "."}
            </>
          : checkResult.status === "cooldown"
            ? `A check just ran. You can check again at ${clockTime(checkResult.nextCheckAt)}.`
          : checkResult.status === "busy"
            ? "We’re still reading your sources. Try again when that finishes."
            : checkResult.status === "idea_cap"
              ? `You have ${checkResult.cap} unused ideas. Use or dismiss some to get more.`
              : checkResult.status === "no_active_sources"
                ? "Your sources need attention before we can check them. See below."
                : null;
  const pageStatus: { icon: typeof Rss; text: ReactNode; action?: ReactNode; tone?: "error" } =
    sources.length === 0
      ? { icon: Plus, text: "Add your website or blog and we’ll find post ideas in it.",
          action: statusLink("Add a website", openAddSourceModal) }
      : extractingSources.length > 0
        ? { icon: Loader2, text: `Reading ${discoveryContext}…${extractionTotalKnown ? ` ${extractionDone} of ${extractionTotal} ${extractionUnit} so far.` : ""} Ideas appear here as we find them.` }
        : stats.pending === 0 && sources.some((source) => source.feedError)
          ? { icon: AlertCircle, tone: "error", text: `We couldn’t finish reading ${sourcesPhrase}. Check the source below.` }
          : configLoading || planLoading
            ? { icon: Rss, text: stats.pending === 0 ? foundSentence : `${foundSentence}.` }
            : !isStarterOrAbove
              ? { icon: Rss, text: foundWith("Get fresh ideas every day on a paid plan."), action: upgradeButton }
              : config?.enabled
                ? { icon: RefreshCw, text: stats.pending === 0 ? `${foundSentence} We check for new content daily.` : foundWith("and check for new content daily."),
                    action: dailyCheckActions }
                : { icon: Rss, text: foundWith("Daily checks are paused."),
                    action: statusLink("Resume", handlePauseResume) };
  const PageStatusIcon = pageStatus.icon;
  const pageStatusLine = (
    <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-border bg-card px-4 py-3">
      <PageStatusIcon
        aria-hidden="true"
        className={`h-5 w-5 shrink-0 ${pageStatus.tone === "error" ? "text-destructive" : "text-brand-ink"} ${PageStatusIcon === Loader2 ? "animate-spin motion-reduce:animate-none" : ""}`}
      />
      <p role="status" className="min-w-0 flex-1 text-sm leading-relaxed">{pageStatus.text}</p>
      {pageStatus.action}
      {checkNotice && (
        <p role="status" className="basis-full border-t border-border pt-2 text-sm leading-relaxed text-muted-foreground">
          {checkNotice}
        </p>
      )}
    </div>
  );
  const sourceMeta = (source: MonitoringSource) => {
    if (source.extracting && !source.feedError) return "Reading now…";
    if (source.feedError) return "Couldn’t read it. Edit to check the address.";
    const read = source.knownGuids?.length || 0;
    const items = source.sourceType === "rss" ? "article" : source.sourceType === "youtube" ? "video" : "page";
    // A channel's listed uploads; only a few a day are read for ideas.
    const verb = source.sourceType === "youtube" ? "Found" : "Read";
    return [
      read ? `${verb} ${read} ${items}${read === 1 ? "" : "s"}` : null,
      source.lastPolledAt ? `checked ${formatRelativeTime(source.lastPolledAt)}` : null,
    ].filter(Boolean).join(" · ") || "Waiting for its first check";
  };
  const sourcesStrip = (
    <section aria-labelledby="idea-sources-title" className="mt-10 border-t border-border pt-6">
      <h2 id="idea-sources-title" className="mb-3 text-sm font-semibold">Where ideas come from</h2>
      <div className="flex flex-wrap items-stretch gap-3">
        {sources.map((source) => (
          <div key={source.id} className="flex min-w-[16rem] max-w-full items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5">
            <img
              src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(sourceHost(source))}&sz=32`}
              alt=""
              referrerPolicy="no-referrer"
              className="h-6 w-6 shrink-0 rounded"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{sourceHost(source)}</p>
              <p className={`truncate text-xs ${source.feedError ? "text-destructive" : "text-muted-foreground"}`}>{sourceMeta(source)}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              aria-label={`Edit ${sourceHost(source)}`}
              onClick={() => openEditSourceModal(source)}
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        ))}
        {sources.length < 3 && (
          <Button variant="outline" className="h-auto min-h-[3.25rem] gap-2" onClick={openAddSourceModal}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add a website or blog
          </Button>
        )}
      </div>
    </section>
  );

  // Someone who skipped setup has no sources: show what Ideas will do.
  const exampleIdeas = [
    { format: "6-slide carousel", title: "5 questions to ask before you hire a contractor" },
    { format: "Quote post", title: "“The best marketing is a happy customer telling a friend.”" },
    { format: "5-slide carousel", title: "Behind the scenes: how we prepare for a busy week" },
  ];
  const noSourcesState = (
    <section aria-labelledby="ideas-empty-title" className="rounded-xl border border-border bg-card px-6 py-10 text-center sm:px-10">
      <span aria-hidden="true" className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-[oklch(var(--brand)/0.1)] text-brand-ink">
        <Lightbulb className="h-6 w-6" />
      </span>
      <h2 id="ideas-empty-title" className="text-xl font-semibold tracking-tight">Get post ideas from your website</h2>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
        Add your website, blog or newsletter. We’ll read it and have post ideas waiting for you.
      </p>
      <Button variant="brand" className="mt-6 h-11 px-6 font-semibold" onClick={openAddSourceModal}>
        <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
        Add your website
      </Button>
      <div aria-hidden="true" className="mt-10 grid gap-3 text-left sm:grid-cols-3">
        {exampleIdeas.map((idea) => (
          <div key={idea.title} className="rounded-lg border border-dashed border-border bg-background p-4 opacity-70">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Example · {idea.format}</p>
            <p className="mt-2 text-sm font-medium leading-snug">{idea.title}</p>
          </div>
        ))}
      </div>
    </section>
  );
  const pageHasNoSources = page && !linkedIdea && sourcesWorkspace === activeWorkspaceId && sources.length === 0;

  const feed = (
    <div className={embedded ? "" : "p-6"}>
      <div className={embedded ? "" : "max-w-2xl mx-auto"}>
        {embedded && !expanded && topics.length > 0 && (
          <div className="mb-2 flex flex-wrap items-center justify-end gap-2 sm:absolute sm:right-0 sm:top-5 sm:mb-0">
            <div className="flex items-center gap-1">
              {totalTopics > 6 && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-haspopup="dialog"
                  onClick={() => {
                    setExpanded(true);
                    setSelectedIdeaId(null);
                  }}
                >
                  {`All (${totalTopics})`}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                aria-haspopup="dialog"
                aria-controls="autopilot-source-configuration"
                onClick={() => {
                  setExpanded(true);
                  setSelectedIdeaId(null);
                  if (sources.length === 0) openAddSourceModal();
                }}
              >
                {sources.length === 0 ? "Add a source" : "Configure"}
              </Button>
            </div>
          </div>
        )}
        {pageHasNoSources ? noSourcesState : page && pageStatusLine}
        {page && statusFilter === "archived" && (
          <h2 className="mb-4 text-base font-semibold">Dismissed ideas</h2>
        )}
        {!page && (
        <div id="autopilot-source-configuration" hidden={embedded && !expanded}>
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Rocket className="h-6 w-6 text-primary" />
                {embedded ? (
                  <h3 className="text-base font-semibold">Sources</h3>
                ) : (
                  <h1 className="text-2xl font-semibold text-gray-900">
                    Autopilot
                  </h1>
                )}
                {configLoading ? (
                  <span className="text-xs text-muted-foreground">Loading settings…</span>
                ) : config?.enabled ? (
                  <span className="relative flex items-center gap-1.5 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
                    </span>
                    Active
                  </span>
                ) : anyExtracting ? (
                  <span className="flex items-center gap-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-0.5">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Setting up
                  </span>
                ) : (
                  <Badge
                    variant="outline"
                    className="text-yellow-600 border-yellow-200 bg-yellow-50 text-xs"
                  >
                    Paused
                  </Badge>
                )}
              </div>
              {config?.enabled ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  disabled={configLoading}
                  onClick={handlePauseResume}
                >
                  <Pause className="h-3.5 w-3.5 mr-1.5" />
                  Pause
                </Button>
              ) : !isStarterOrAbove ? (
                <Button
                  size="sm"
                  className="h-8 bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() =>
                    navigateToUpgrade({ reason: "autopilot_activate" })
                  }
                >
                  <Rocket className="h-3.5 w-3.5 mr-1.5" />
                  Upgrade to Activate
                </Button>
              ) : (
                <Button size="sm" className="h-8" onClick={handlePauseResume}>
                  <Play className="h-3.5 w-3.5 mr-1.5" />
                  Resume
                </Button>
              )}
            </div>
            {/* Source Carousel */}
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin">
              {sources.map((source) => (
                <SourceCard
                  key={source.id}
                  source={source}
                  connectionsById={connectionsById}
                  brandKits={userBrandKits}
                  topicCount={
                    rawTopics.filter(
                      (t) => t.sourceId === source.id && t.status === "pending",
                    ).length
                  }
                  formatRelativeTime={formatRelativeTime}
                  onClick={() => openEditSourceModal(source)}
                  onFilterBySource={() =>
                    setSourceFilter(
                      source.id === sourceFilter ? "all" : source.id,
                    )
                  }
                  isFiltered={sourceFilter === source.id}
                />
              ))}

              {/* Quick-Add ghost card */}
              {sources.length < 3 && (
                <button
                  onClick={openAddSourceModal}
                  className="shrink-0 w-[200px] rounded-xl border-2 border-dashed border-gray-200 bg-muted/20 flex flex-col items-center justify-center gap-2 py-6 hover:border-primary/40 hover:bg-primary/5 transition-all group"
                >
                  <div className="h-9 w-9 rounded-full bg-gray-100 group-hover:bg-primary/10 flex items-center justify-center transition-colors">
                    <Plus className="h-4 w-4 text-gray-400 group-hover:text-primary transition-colors" />
                  </div>
                  <span className="text-xs font-medium text-gray-400 group-hover:text-primary transition-colors">
                    Add Content Source
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
        )}

        <div>
          {/* Filters */}
          {(!embedded || expanded) && !page && (
            <div className="mb-4">
              <Tabs value={statusFilter} onValueChange={setStatusFilter}>
                <TabsList className="grid h-11 w-full grid-cols-3 rounded-lg border bg-muted/30 p-1">
                  <TabsTrigger
                    value="all"
                    className="rounded-md text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                  >
                    All
                  </TabsTrigger>
                  <TabsTrigger
                    value="pending"
                    className="rounded-md text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                  >
                    Pending
                    {stats.pending > 0 && (
                      <Badge
                        variant="secondary"
                        className="ml-1.5 text-xs px-1.5 py-0 hidden sm:inline-flex"
                      >
                        {stats.pending}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger
                    value="archived"
                    className="rounded-md text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                  >
                    Archived
                    {stats.archived > 0 && (
                      <Badge
                        variant="secondary"
                        className="ml-1.5 text-xs px-1.5 py-0 hidden sm:inline-flex"
                      >
                        {stats.archived}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}

          {embedded &&
            !expanded &&
            topics.length > 0 &&
            (extractingSources.length > 0) &&
            extractionProgress}
          {/* Topic Cards */}
          {(loading && topics.length === 0) ||
          (configLoading && topics.length === 0) ? (
            embedded && !expanded ? (
              <p role="status" className="py-3 text-sm text-muted-foreground">Loading ideas…</p>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="h-7 w-7 animate-spin text-primary/60 mb-3" />
                <p className="text-sm text-muted-foreground">Loading ideas…</p>
              </div>
            )
          ) : ideasError ? (
            <div role="alert" className="py-8 text-center">
              <p className="mb-3 text-sm text-muted-foreground">
                Couldn’t load your ideas.
              </p>
              <Button
                variant="outline"
                onClick={() => setIdeasRetry((retry) => retry + 1)}
              >
                Try again
              </Button>
            </div>
          ) : pageHasNoSources ? null : page && topics.length === 0 && statusFilter === "pending" ? (
            // The status line above already explains what's happening.
            <div className="py-6" />
          ) : topics.length === 0 &&
            sources.some((source) => source.feedError) ? (
            <div
              role="alert"
              className="mx-auto max-w-md px-4 py-8 text-center"
            >
              <p className="text-sm font-medium">
                We couldn’t finish finding ideas.
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Your source is saved. Check its configuration to continue.
              </p>
              <Button variant="link" onClick={() => setExpanded(true)}>
                View sources
              </Button>
            </div>
          ) : topics.length === 0 && (extractingSources.length > 0) ? (
            extractionProgress
          ) : topics.length === 0 && embedded && !expanded ? (
            <PostIdeasEmptyState
              hasSources={sources.length > 0}
              onGetStarted={() => {
                setSelectedIdeaId(null);
                if (sources.length === 0) {
                  openAddSourceModal();
                } else {
                  setExpanded(true);
                }
              }}
            />
          ) : topics.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                {statusFilter === "all" || statusFilter === "pending" ? (
                  <Inbox className="h-6 w-6 text-muted-foreground/50" />
                ) : statusFilter === "generated" ? (
                  <Sparkles className="h-6 w-6 text-muted-foreground/50" />
                ) : (
                  <Archive className="h-6 w-6 text-muted-foreground/50" />
                )}
              </div>
              <p className="font-medium text-sm text-foreground/80 mb-1">
                {statusFilter === "all"
                  ? "No ideas yet"
                  : statusFilter === "pending"
                    ? "No new ideas"
                    : statusFilter === "generated"
                      ? "No generated posts yet"
                      : "No archived suggestions"}
              </p>
              <p className="text-xs text-muted-foreground max-w-[280px]">
                {statusFilter === "all" || statusFilter === "pending"
                  ? "New ideas show up here as we check your sources."
                  : statusFilter === "generated"
                    ? "Generate visuals for a suggested post to see it here."
                    : "Suggestions you dismiss will appear here for 30 days."}
              </p>
            </div>
          ) : embedded && !expanded ? (
            <div
              className="flex items-stretch gap-3 overflow-x-auto pb-2 pt-1"
              aria-label="Suggested posts"
            >
              {topics.map((topic) => {
                const selected = selectedIdeaId === topic.id;
                if (handoffs[topic.id]?.drafted) {
                  return (
                    <div key={topic.id} className="w-80 max-w-[calc(100vw-3rem)] shrink-0">
                      <DraftHandoffCard title={getTopicTitle(topic)} />
                    </div>
                  );
                }
                return (
                  <div key={topic.id} className="w-80 max-w-[calc(100vw-3rem)] shrink-0">
                    <TopicCard
                      compact
                      preview
                      brandAssets={brandAssetsFor(topic)}
                      onUpgrade={
                        outOfCredits
                          ? () => navigateToUpgrade({ reason: "no_credits" })
                          : undefined
                      }
                      previewOpen={selected}
                      onPreviewOpenChange={(open) =>
                        changeIdeaPreview(topic.id, open)
                      }
                      topic={topic}
                      displayTab={statusFilter}
                      onGenerate={handleRowGenerate}
                      onConfigure={configureIdea}
                      hasLastSettings={!!generationDefaults(topic).saved}
                      onArchive={handleArchive}
                      onUnarchive={handleUnarchive}
                      onViewPost={handleViewPost}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              className={
                embedded
                  ? "grid grid-cols-1 gap-3 lg:grid-cols-3"
                  : "space-y-2.5"
              }
            >
              {topics.map((topic) => page && handoffs[topic.id]?.drafted ? (
                <DraftHandoffCard key={topic.id} title={getTopicTitle(topic)} onOpenDrafts={onOpenDrafts} />
              ) : embedded ? (
                // The full view reuses the strip's card, including its detail view.
                <TopicCard
                  key={topic.id}
                  sourceLabel={page ? sourceLabelFor(topic) : undefined}
                  compact
                  preview
                  brandAssets={brandAssetsFor(topic)}
                  onUpgrade={
                    outOfCredits
                      ? () => navigateToUpgrade({ reason: "no_credits" })
                      : undefined
                  }
                  previewOpen={selectedIdeaId === topic.id}
                  onPreviewOpenChange={(open) =>
                    changeIdeaPreview(topic.id, open)
                  }
                  topic={topic}
                  displayTab={statusFilter}
                  onGenerate={page ? handleRowGenerate : handleGenerate}
                  onConfigure={configureIdea}
                  hasLastSettings={!!generationDefaults(topic).saved}
                  onArchive={handleArchive}
                  onUnarchive={handleUnarchive}
                  onViewPost={handleViewPost}
                />
              ) : (
                <TopicCard
                  key={topic.id}
                  topic={topic}
                  displayTab={statusFilter}
                  onGenerate={handleGenerate}
                  onConfigure={configureIdea}
                  hasLastSettings={!!generationDefaults(topic).saved}
                  onArchive={handleArchive}
                  onUnarchive={handleUnarchive}
                  onViewPost={handleViewPost}
                />
              ))}
              {(!embedded || expanded) && topics.length < totalTopics && (
                <Button
                  variant="outline"
                  className="w-full h-9 text-xs text-muted-foreground col-span-full"
                  onClick={loadMore}
                >
                  Show more ({totalTopics - topics.length} remaining)
                </Button>
              )}
              {/* Retention policy footnote */}
              {statusFilter === "pending" && (!embedded || expanded) && (
                <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground/50 pt-4 col-span-full">
                  <Clock className="h-3 w-3" />
                  {page
                    ? "Ideas you don’t draft are cleared after 30 days"
                    : "Suggestions are automatically archived 30 days after discovery"}
                </p>
              )}
              {statusFilter === "archived" && (
                <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground/50 pt-4 col-span-full">
                  <Clock className="h-3 w-3" />
                  {page
                    ? "Dismissed ideas are deleted after 30 days"
                    : "Archived suggestions are permanently deleted after 30 days"}
                </p>
              )}
            </div>
          )}
          {/* Dismissed ideas stay one click away instead of in a tab. */}
          {page && (statusFilter === "archived" || stats.archived > 0) && (
            <div className="mt-6 flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => setStatusFilter(statusFilter === "archived" ? "pending" : "archived")}
              >
                {statusFilter === "archived" ? "Back to new ideas" : `Dismissed ideas (${stats.archived})`}
              </Button>
            </div>
          )}
          {page && !pageHasNoSources && sourcesStrip}
        </div>
      </div>

      {/* Post Viewer */}
      {viewingPostId && viewingPost && (
        <PostViewer
          isOpen={!!viewingPostId}
          onClose={() => {
            setViewingPostId(null);
            setViewingPost(null);
          }}
          expandedPost={viewingPost}
          initialIndex={0}
          connectionsById={connectionsById}
          enableLiveUpdates={true}
        />
      )}

      {/* Unified Source Modal (Add + Edit) */}
      <Dialog
        open={sourceModal.open}
        onOpenChange={(open) => {
          if (!open) {
            if (sourceModal.mode === "activate") {
              onCancelPending?.();
            }
            closeSourceModal();
          }
        }}
      >
        <DialogContent className="w-full sm:w-[28rem] sm:max-w-md min-h-[420px] max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {sourceModal.mode === "activate"
                ? "Start finding ideas"
                : sourceModal.mode === "add"
                  ? "Add a website or blog"
                  : sourceModal.feedTitle || "Edit Content Source"}
            </DialogTitle>
          </DialogHeader>

          <div
            className={`relative flex-1 overflow-x-hidden ${sourceModal.mode === "add" && !sourceModal.discovered && !sectionCandidates ? "overflow-y-hidden" : "overflow-y-auto"}`}
          >
            {/* Step 1 overlay: URL discovery (centered) */}
            {sourceModal.mode === "add" &&
              !sourceModal.discovered &&
              !sectionCandidates && (
                <div className="absolute inset-0 z-10 flex items-center justify-center">
                  <div className="w-full max-w-sm mx-auto px-2 space-y-4">
                    <div className="text-center mb-6">
                      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                        <Rss className="h-6 w-6 text-primary" />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Paste your website, blog or newsletter address. We’ll read it
                        and turn what we find into post ideas.
                      </p>
                    </div>
                    <div className="space-y-3">
                      <Label
                        htmlFor="post-ideas-source-url"
                        className="block text-sm font-medium"
                      >
                        Website URL
                      </Label>
                      <Input
                        id="post-ideas-source-url"
                        placeholder="e.g. example.com/blog"
                        inputMode="url"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        value={sourceModal.url}
                        onChange={(e) => {
                          sourceUrlEdited.current = true;
                          setSourceModal((prev) => ({
                            ...prev,
                            url: e.target.value,
                          }));
                        }}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleDiscoverFeed()
                        }
                        autoFocus
                        className="h-11"
                      />
                      <Button
                        onClick={handleDiscoverFeed}
                        disabled={discovering || !sourceModal.url.trim()}
                        className="w-full h-11 px-5"
                      >
                        {discovering ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Find ideas"
                        )}
                      </Button>
                    </div>
                    {discoverError && (
                      <p className="text-xs text-red-600 font-medium text-center">
                        {discoverError}
                      </p>
                    )}
                  </div>
                </div>
              )}

            {/* Step 1b: Section picker (sitemap discovery returned multiple candidates) */}
            {sourceModal.mode === "add" &&
              !sourceModal.discovered &&
              sectionCandidates && (
                <div className="px-1 py-2">
                  <SectionPicker
                    candidates={sectionCandidates}
                    hostname={(() => {
                      try {
                        return new URL(sourceModal.url).hostname;
                      } catch {
                        return sourceModal.url;
                      }
                    })()}
                    loading={discovering}
                    onSelect={handleSectionSelectInModal}
                    onBack={() => {
                      setSectionCandidates(null);
                      setDiscoverError(null);
                    }}
                  />
                  {discoverError && (
                    <p className="text-xs text-red-600 font-medium text-center mt-3">
                      {discoverError}
                    </p>
                  )}
                </div>
              )}

            {/* Step 2: Config form (revealed after discovery or for edit) */}
            {(sourceModal.discovered || sourceModal.mode === "edit") && (
              <div className="space-y-4 pt-2 pb-4 animate-in fade-in duration-300">
                {/* Source header */}
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/40 border">
                  <SourceFavicon url={sourceModal.url} size="md" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {sourceModal.feedTitle || sourceModal.url}
                    </p>
                    <div className="flex items-center gap-2">
                      <a
                        href={sourceModal.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                      >
                        {(() => {
                          try {
                            return new URL(sourceModal.url).hostname;
                          } catch {
                            return sourceModal.url;
                          }
                        })()}
                        <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    </div>
                    {(sourceModal.mode === "add" ||
                      sourceModal.mode === "activate") &&
                      (() => {
                        const discoverySummary =
                          getDiscoverySummary(sourceModal);
                        if (!discoverySummary) return null;
                        const coverageNote = getSiteCoverageNote(
                          sourceModal.url,
                          sourceModal.sections,
                        );
                        return (
                          <>
                            <p className="mt-1 text-[11px] font-medium text-green-600">
                              {discoverySummary}
                            </p>
                            {coverageNote && (
                              <p className="mt-1 text-[11px] text-muted-foreground">
                                {coverageNote}
                              </p>
                            )}
                          </>
                        );
                      })()}
                    {sourceModal.mode === "edit" &&
                      (() => {
                        const note = getWholeSiteSettingsNote(sourceModal);
                        return note ? (
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            {note}
                          </p>
                        ) : null;
                      })()}
                  </div>
                  {sourceModal.mode === "add" && (
                    <Check className="h-4 w-4 text-green-600 shrink-0" />
                  )}
                </div>

                {/* Content type */}
                {
                  <div>
                    <Label className="mb-2 block">
                      How should we use this content?
                    </Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          setSourceModal((prev) => ({
                            ...prev,
                            isOwned: true,
                            fallbackMode:
                              prev.mode === "edit"
                                ? prev.fallbackMode
                                : defaultFallbackMode(true),
                          }))
                        }
                        className={`rounded-lg border p-3 text-left transition-colors ${
                          sourceModal.isOwned === true
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-muted-foreground/40"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <User className="h-3.5 w-3.5 text-blue-600" />
                          <p className="text-sm font-medium">First-party</p>
                          {sourceModal.isOwned === true && (
                            <Check className="h-3 w-3 text-primary ml-auto" />
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          Your own content, repurposed in first person.
                        </p>
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setSourceModal((prev) => ({
                            ...prev,
                            isOwned: false,
                            fallbackMode:
                              prev.mode === "edit"
                                ? prev.fallbackMode
                                : defaultFallbackMode(false),
                          }))
                        }
                        className={`rounded-lg border p-3 text-left transition-colors ${
                          sourceModal.isOwned === false
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-muted-foreground/40"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Globe className="h-3.5 w-3.5 text-amber-600" />
                          <p className="text-sm font-medium">Curation</p>
                          {sourceModal.isOwned === false && (
                            <Check className="h-3 w-3 text-primary ml-auto" />
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          Third-party content with fresh takes and attribution.
                        </p>
                      </button>
                    </div>
                  </div>
                }

                {/* Freshness mode */}
                <div>
                  <Label className="mb-2 block">
                    When there's no new content
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        setSourceModal((prev) => ({
                          ...prev,
                          fallbackMode: "new_only",
                        }))
                      }
                      className={`rounded-lg border p-3 text-left transition-colors ${
                        sourceModal.fallbackMode === "new_only"
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-muted-foreground/40"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="h-3.5 w-3.5 text-sky-600" />
                        <p className="text-sm font-medium">Wait for new</p>
                        {sourceModal.fallbackMode === "new_only" && (
                          <Check className="h-3 w-3 text-primary ml-auto" />
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Best for news and time-sensitive curation.
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setSourceModal((prev) => ({
                          ...prev,
                          fallbackMode: "reuse_library",
                        }))
                      }
                      className={`rounded-lg border p-3 text-left transition-colors ${
                        sourceModal.fallbackMode === "reuse_library"
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-muted-foreground/40"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Archive className="h-3.5 w-3.5 text-emerald-600" />
                        <p className="text-sm font-medium">Reuse older</p>
                        {sourceModal.fallbackMode === "reuse_library" && (
                          <Check className="h-3 w-3 text-primary ml-auto" />
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Best for evergreen content and owned libraries.
                      </p>
                    </button>
                  </div>
                </div>

                {/* Brand Kit */}
                <BrandKitSelector
                  brandKits={userBrandKits}
                  selectedKitId={sourceModal.brandKitId}
                  onChange={(kitId) =>
                    setSourceModal((prev) => ({ ...prev, brandKitId: kitId }))
                  }
                />

                {/* Language */}
                <div>
                  <Label className="text-sm font-semibold mb-2 block">
                    Output Language{" "}
                    <span className="text-xs font-normal text-muted-foreground">
                      (optional)
                    </span>
                  </Label>
                  <Select
                    value={sourceModal.targetLanguage}
                    onValueChange={(v) =>
                      setSourceModal((prev) => ({ ...prev, targetLanguage: v }))
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {POPULAR_LANGUAGES.map((lang) => (
                        <SelectItem key={lang.value} value={lang.value}>
                          {lang.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Pre-fill note */}
                {sourceModal.mode === "add" && sources.length > 0 && (
                  <p className="text-[11px] text-muted-foreground bg-muted/40 rounded-md px-3 py-2">
                    Pre-filled from <strong>{sources[0].name}</strong>'s config
                    to keep your branding consistent.
                  </p>
                )}

                {discoverError && sourceModal.discovered && (
                  <p className="text-xs text-red-600 font-medium">
                    {discoverError}
                  </p>
                )}
              </div>
            )}
          </div>

          {(sourceModal.discovered ||
            sourceModal.mode === "edit" ||
            sourceModal.mode === "activate") && (
            <>
              {(sourceModal.mode === "activate" ||
                sourceModal.mode === "add") && (
                <p className="text-xs text-muted-foreground text-center max-w-md mx-auto">
                  {fallbackModeFooter(sourceModal.fallbackMode)}
                </p>
              )}
              <DialogFooter className="!justify-between">
                {sourceModal.mode === "edit" && sourceModal.sourceId ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs"
                    onClick={() => handleDeleteSource(sourceModal.sourceId!)}
                    disabled={savingSource || configLoading}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Delete Content Source
                  </Button>
                ) : (
                  <div />
                )}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (sourceModal.mode === "activate") {
                        onCancelPending?.();
                      }
                      closeSourceModal();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveSourceModal}
                    disabled={savingSource || configLoading}
                  >
                    {savingSource ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Saving...
                      </>
                    ) : sourceModal.mode === "activate" ? (
                      <>
                        <Rocket className="h-4 w-4 mr-1" />
                        Start finding ideas
                      </>
                    ) : sourceModal.mode === "add" ? (
                      <>
                        <Rss className="h-4 w-4 mr-1" />
                        Add source
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
  return <>
    {embedded && !page ? (
    <ExpandingWorkspace
      title="Suggested Posts"
      expanded={expanded}
      onClose={() => {
        setExpanded(false);
        if (ideasRequested) {
          setSearchParams(current => {
            const next = new URLSearchParams(current);
            next.delete("ideas");
            return next;
          }, { replace: true });
        }
        setStatusFilter("pending");
        setSourceFilter("all");
        setDisplayLimit(20);
      }}
    >
      {feed}
    </ExpandingWorkspace>
  ) : (
    feed
    )}
    {configuringIdea && (
      <IdeaGenerationWorkspace
        key={`${activeWorkspaceId}:${configuringIdea.id}`}
        topic={configuringIdea}
        defaults={configurationRecovery?.settings ?? generationDefaults(configuringIdea).defaults}
        notice={configurationRecovery?.message}
        brandKits={userBrandKits}
        connections={workspaceConnections.filter(c => c.status === "active" || c.status === "expiring_soon")}
        onGenerate={settings =>
          pendingPreview || page
            ? handleRowGenerate(configuringIdea.id, settings)
            : handleGenerate(configuringIdea.id, settings)
        }
        onClose={() => { setConfiguringIdea(null); setConfigurationRecovery(null); }}
      />
    )}
  </>;
}

// ---- Source Card Component ----

function SourceFavicon({
  url,
  size = "sm",
}: {
  url: string;
  size?: "sm" | "md";
}) {
  const [error, setError] = useState(false);
  const faviconUrl = useMemo(() => {
    try {
      return `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=32`;
    } catch {
      return null;
    }
  }, [url]);

  const px = size === "md" ? "h-5 w-5" : "h-3.5 w-3.5";
  if (!faviconUrl || error) return <Rss className={`${px} text-orange-500`} />;
  return (
    <img
      src={faviconUrl}
      alt=""
      className={`${px} rounded-sm`}
      onError={() => setError(true)}
    />
  );
}

interface SourceCardProps {
  source: MonitoringSource;
  connectionsById: Record<string, PlatformConnection>;
  brandKits: BrandKit[];
  topicCount: number;
  formatRelativeTime: (ts: number) => string;
  onClick: () => void;
  onFilterBySource: () => void;
  isFiltered: boolean;
}

function SourceCard({
  source,
  connectionsById,
  brandKits,
  topicCount,
  formatRelativeTime,
  onClick,
  onFilterBySource,
  isFiltered,
}: SourceCardProps) {
  const sourceConns = (source.connectionIds || [])
    .map((id) => connectionsById[id])
    .filter(Boolean);
  const kit = brandKits.find((k) => k.id === source.brandKitId);

  const isExtracting = source.extracting;
  const done = source.extractionDone || 0;
  const total = source.extractionTotal || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const borderClass = isFiltered
    ? "border-primary shadow-md"
    : source.status === "error"
      ? "border-red-200"
      : isExtracting
        ? "border-blue-200"
        : "border-gray-200 hover:border-gray-300";

  return (
    <div
      className={`shrink-0 w-[200px] rounded-xl border bg-card p-3.5 text-left transition-all hover:shadow-md cursor-pointer ${borderClass}`}
      onClick={onFilterBySource}
    >
      {/* Header: Favicon + Name + Edit */}
      <div className="flex items-center gap-2 mb-2">
        <SourceFavicon url={source.url} size="md" />
        <span className="font-medium text-sm text-gray-900 truncate flex-1">
          {source.name}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="h-5 w-5 rounded flex items-center justify-center text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted transition-colors shrink-0"
          title={`Edit ${source.name}`}
        >
          <Pencil className="h-3 w-3" />
        </button>
      </div>

      {/* Live status */}
      <div className="mb-2.5">
        {isExtracting ? (
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
              <span className="text-[11px] font-medium text-blue-600 truncate">
                {total > 0
                  ? `Processing ${total} most recent article${total !== 1 ? "s" : ""}...`
                  : "Starting..."}
              </span>
              {total > 0 && (
                <span className="text-[10px] text-blue-500 tabular-nums ml-auto shrink-0">
                  {done}/{total}
                </span>
              )}
            </div>
            {total > 0 && (
              <div className="h-1 bg-blue-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            )}
          </div>
        ) : source.status === "error" ? (
          <div className="flex items-center gap-1.5">
            <AlertCircle className="h-3 w-3 text-red-500" />
            <span className="text-[11px] font-medium text-red-600">
              Feed error
            </span>
          </div>
        ) : topicCount > 0 ? (
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-green-500" />
            <span className="text-[11px] font-medium text-green-700">
              {topicCount} pending suggestion{topicCount !== 1 && "s"}
            </span>
          </div>
        ) : source.lastPolledAt ? (
          <span className="text-[11px] text-muted-foreground">
            Scanned {formatRelativeTime(source.lastPolledAt)}
          </span>
        ) : (
          <span className="text-[11px] text-muted-foreground italic">
            Waiting for first scan
          </span>
        )}
      </div>

      {/* Mini config summary */}
      <div className="flex items-center gap-1.5 min-h-[20px]">
        {sourceConns.length > 0 && (
          <div className="flex -space-x-1">
            {sourceConns.slice(0, 3).map((conn) => (
              <span key={conn.id} className="inline-flex">
                <PlatformConnectionIcon
                  platform={conn.platform}
                  profileImage={conn.profileImage}
                  profileName={conn.profileName}
                  size="xs"
                  disableTooltip
                />
              </span>
            ))}
          </div>
        )}
        {kit && (
          <span
            className="text-[10px] text-muted-foreground truncate max-w-[80px]"
            title={kit.name}
          >
            {kit.name}
          </span>
        )}
        {!sourceConns.length && !kit && (
          <span className="text-[10px] text-muted-foreground/50 italic">
            No config set
          </span>
        )}
      </div>
    </div>
  );
}

const HANDOFF_MS = 2500;
// The Ideas page leaves time to read "It'll be in Drafts" and follow it.
const PAGE_HANDOFF_MS = 6000;

function getTopicTitle(topic: PendingTopicData) {
  return topic.content.category === "quote"
    ? topic.content.content?.quote || ""
    : topic.content.content?.title || "";
}

/** Stands in for a drafted idea until the next one takes its place. */
function DraftHandoffCard({ title, onOpenDrafts }: { title: string; onOpenDrafts?: () => void }) {
  return (
    <div className="flex h-full min-h-48 flex-col justify-center gap-3 rounded-lg border border-dashed border-border bg-muted/20 p-5">
      <p className="line-clamp-2 text-sm font-medium leading-5">{title}</p>
      <p role="status" className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin motion-reduce:animate-none" />
        Drafting
      </p>
      {onOpenDrafts ? (
        <button
          type="button"
          onClick={onOpenDrafts}
          className="inline-flex items-center gap-1 self-start text-sm font-medium text-brand-ink underline-offset-2 hover:underline"
        >
          It’ll be in Drafts
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ) : (
        <a
          href="#studio-drafts"
          onClick={(event) => {
            const drafts = document.getElementById("studio-drafts");
            if (!drafts) return;
            event.preventDefault();
            drafts.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className="inline-flex items-center gap-1 text-sm font-medium text-brand-ink underline-offset-2 hover:underline"
        >
          See it in Recent drafts
          <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      )}
    </div>
  );
}
